import React, { useEffect, useState } from 'react';
import AmountInput from '../ui/AmountInput';
import { extractApiError } from '../../utils/errorMessages';
import { canonicalizeAmount } from '../../utils/amountFormat';
import {
    createInstrument,
    fetchBenchmarks,
    updateInstrument,
} from '../../services/paperPricingService';
import {
    CONTRACT_PERIODS,
    INSTRUMENT_TYPES,
    SETTLEMENT_TYPES,
    fieldErrorsFrom,
    requireList,
    unitLabel,
    unplacedFieldErrors,
} from './paperPricingFormat';
import { FormField } from './PaperPricingParts';
import PaperPricingModal from './PaperPricingModal';

const FORM_FIELDS = [
    'name', 'instrument_type', 'benchmark_id', 'currency', 'contract_unit', 'settlement_basis',
    'contract_period', 'settlement_type', 'minimum_quantity', 'rounding_decimals',
];

const BLANK = {
    name: '',
    instrument_type: '',
    benchmark_id: '',
    currency: 'USD',
    contract_unit: '',
    settlement_basis: '',
    contract_period: '',
    settlement_type: '',
    minimum_quantity: '',
    rounding_decimals: '2',
};

const fromInstrument = (instrument) => ({
    name: instrument.name,
    instrument_type: instrument.instrument_type,
    benchmark_id: String(instrument.benchmark.id),
    currency: instrument.currency,
    contract_unit: instrument.contract_unit,
    settlement_basis: instrument.settlement_basis ?? '',
    contract_period: instrument.contract_period ?? '',
    settlement_type: instrument.settlement_type ?? '',
    minimum_quantity: canonicalizeAmount(instrument.minimum_quantity),
    rounding_decimals: String(instrument.rounding_decimals),
});

const asCode = (text) => text.toUpperCase().replace(/\s+/g, '');

/**
 * Add or edit a paper instrument (ledger row 132, AC3).
 *
 * The underlying benchmark is picked from the ACTIVE list. An instrument whose
 * benchmark has since been switched off shows it, marked, so the person sees
 * what it settles off today; the server refuses saving it that way and names
 * the benchmark, and that sentence appears under the picker.
 */
const InstrumentFormModal = ({ instrument, onClose, onSaved }) => {
    const editing = Boolean(instrument);
    const [form, setForm] = useState(() => (editing ? fromInstrument(instrument) : BLANK));
    const [benchmarks, setBenchmarks] = useState(null);
    const [listError, setListError] = useState('');
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchBenchmarks({ active: 'true' })
            .then((data) => {
                const results = requireList(data, 'results');
                if (!cancelled) setBenchmarks(results);
            })
            .catch((err) => {
                if (!cancelled) {
                    setListError(extractApiError(err, 'The benchmark list could not be loaded. Close this window and try again.'));
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
    const setUpper = (field) => (event) => setForm((current) => ({ ...current, [field]: asCode(event.target.value) }));

    // The switched-off benchmark an existing instrument still points at, kept
    // visible in the picker rather than silently shown as another one.
    const current = editing ? instrument.benchmark : null;
    const currentMissing = current && benchmarks && !benchmarks.some((benchmark) => benchmark.id === current.id);

    const pickBenchmark = (event) => {
        const id = event.target.value;
        const picked = (benchmarks || []).find((benchmark) => String(benchmark.id) === id);
        setForm((existing) => ({
            ...existing,
            benchmark_id: id,
            // Only fills boxes still empty; anything typed stays as typed.
            currency: existing.currency || (picked ? picked.currency : ''),
            contract_unit: existing.contract_unit || (picked ? picked.unit : ''),
        }));
    };

    const submit = async (event) => {
        event.preventDefault();
        const problems = {};
        if (!form.name.trim()) problems.name = 'Enter the instrument name.';
        if (!form.instrument_type) problems.instrument_type = 'Pick swap or future.';
        if (!form.benchmark_id) problems.benchmark_id = 'Pick the benchmark this instrument settles off.';
        if (!form.currency) problems.currency = 'Enter the currency, e.g. USD.';
        if (!form.contract_unit) problems.contract_unit = 'Enter the contract unit, e.g. MT.';
        if (form.rounding_decimals === '') problems.rounding_decimals = 'Enter how many decimals prices round to, usually 2.';
        setErrors(problems);
        setFormError('');
        if (Object.keys(problems).length) return;

        const payload = {
            name: form.name.trim(),
            instrument_type: form.instrument_type,
            benchmark_id: Number(form.benchmark_id),
            currency: form.currency,
            contract_unit: form.contract_unit,
            settlement_basis: form.settlement_basis.trim(),
            contract_period: form.contract_period,
            settlement_type: form.settlement_type,
            // Optional: an empty box is "no minimum", not zero.
            minimum_quantity: form.minimum_quantity === '' ? null : form.minimum_quantity,
            rounding_decimals: Number(form.rounding_decimals),
        };

        setSaving(true);
        try {
            const saved = editing
                ? await updateInstrument(instrument.id, payload)
                : await createInstrument(payload);
            onSaved(saved, editing);
        } catch (err) {
            const fields = fieldErrorsFrom(err);
            if (fields) {
                setErrors(fields);
                setFormError(unplacedFieldErrors(fields, FORM_FIELDS) || 'Fix the fields marked below, then save again.');
            } else {
                setFormError(extractApiError(err, 'The instrument could not be saved. Try again.'));
            }
            setSaving(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="pp-btn pp-btn--secondary" onClick={onClose} disabled={saving}>
                Cancel
            </button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving || !benchmarks}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add instrument'}
            </button>
        </>
    );

    return (
        <PaperPricingModal
            title={editing ? `Edit ${instrument.name}` : 'Add instrument'}
            eyebrow="Swap & futures instruments"
            onClose={onClose}
            busy={saving}
            wide
            onSubmit={submit}
            footer={footer}
        >
            {formError ? <div className="pp-alert pp-alert--error" role="alert">{formError}</div> : null}
            {listError ? <div className="pp-alert pp-alert--error" role="alert">{listError}</div> : null}

            <div className="pp-form-grid">
                <div className="pp-form-wide">
                    <FormField label="Instrument name" htmlFor="pp-in-name" required error={errors.name}>
                        <input
                            id="pp-in-name"
                            className="pp-input"
                            value={form.name}
                            onChange={set('name')}
                            placeholder="e.g. Singapore Marine Fuel 0.5% Swap"
                            maxLength={200}
                            disabled={saving}
                        />
                    </FormField>
                </div>

                <FormField label="Instrument type" htmlFor="pp-in-type" required error={errors.instrument_type}>
                    <select
                        id="pp-in-type"
                        className="pp-select"
                        value={form.instrument_type}
                        onChange={set('instrument_type')}
                        disabled={saving}
                    >
                        <option value="">Pick a type…</option>
                        {INSTRUMENT_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FormField>

                <FormField
                    label="Underlying benchmark"
                    htmlFor="pp-in-benchmark"
                    required
                    error={errors.benchmark_id}
                    hint="Active benchmarks only."
                >
                    <select
                        id="pp-in-benchmark"
                        className="pp-select"
                        value={form.benchmark_id}
                        onChange={pickBenchmark}
                        disabled={!benchmarks || saving}
                    >
                        <option value="">{benchmarks ? 'Pick a benchmark…' : 'Loading benchmarks…'}</option>
                        {currentMissing ? (
                            <option value={String(current.id)}>
                                {current.code} · {current.name} (switched off)
                            </option>
                        ) : null}
                        {(benchmarks || []).map((benchmark) => (
                            <option key={benchmark.id} value={String(benchmark.id)}>
                                {benchmark.code} · {benchmark.name} ({unitLabel(benchmark.currency, benchmark.unit)})
                            </option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Currency" htmlFor="pp-in-currency" required error={errors.currency}>
                    <input
                        id="pp-in-currency"
                        className="pp-input pp-input--code"
                        value={form.currency}
                        onChange={setUpper('currency')}
                        maxLength={3}
                        disabled={saving}
                        autoComplete="off"
                    />
                </FormField>

                <FormField
                    label="Contract unit"
                    htmlFor="pp-in-unit"
                    required
                    error={errors.contract_unit}
                    hint="e.g. MT or BBL."
                >
                    <input
                        id="pp-in-unit"
                        className="pp-input pp-input--code"
                        value={form.contract_unit}
                        onChange={setUpper('contract_unit')}
                        maxLength={10}
                        disabled={saving}
                        autoComplete="off"
                    />
                </FormField>

                <div className="pp-form-wide">
                    <FormField label="Settlement basis" htmlFor="pp-in-basis" error={errors.settlement_basis}>
                        <input
                            id="pp-in-basis"
                            className="pp-input"
                            value={form.settlement_basis}
                            onChange={set('settlement_basis')}
                            placeholder="e.g. Monthly average of published Close assessments"
                            maxLength={255}
                            disabled={saving}
                        />
                    </FormField>
                </div>

                <FormField label="Contract periods" htmlFor="pp-in-period" error={errors.contract_period}>
                    <select
                        id="pp-in-period"
                        className="pp-select"
                        value={form.contract_period}
                        onChange={set('contract_period')}
                        disabled={saving}
                    >
                        <option value="">Not set</option>
                        {CONTRACT_PERIODS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Cash or physical" htmlFor="pp-in-settlement" error={errors.settlement_type}>
                    <select
                        id="pp-in-settlement"
                        className="pp-select"
                        value={form.settlement_type}
                        onChange={set('settlement_type')}
                        disabled={saving}
                    >
                        <option value="">Not set</option>
                        {SETTLEMENT_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FormField>

                <FormField
                    label="Minimum quantity"
                    htmlFor="pp-in-minimum"
                    error={errors.minimum_quantity}
                    hint={form.contract_unit ? `In ${form.contract_unit}. Leave empty for no minimum.` : 'Leave empty for no minimum.'}
                >
                    <AmountInput
                        id="pp-in-minimum"
                        className="pp-input pp-input--number"
                        value={form.minimum_quantity}
                        onChange={(value) => setForm((existing) => ({ ...existing, minimum_quantity: value }))}
                        maxDecimals={3}
                        disabled={saving}
                    />
                </FormField>

                <FormField
                    label="Rounding (decimals)"
                    htmlFor="pp-in-rounding"
                    required
                    error={errors.rounding_decimals}
                >
                    <AmountInput
                        id="pp-in-rounding"
                        kind="integer"
                        className="pp-input pp-input--number"
                        value={form.rounding_decimals}
                        onChange={(value) => setForm((existing) => ({ ...existing, rounding_decimals: value }))}
                        disabled={saving}
                    />
                </FormField>
            </div>
        </PaperPricingModal>
    );
};

export default InstrumentFormModal;
